import { useMemo, useState } from "react";
import { CheckIcon, MagnifyingGlassIcon, PlusIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { cn } from "@utils";
import {
  FIELD_GROUPS,
  INCLUDE_EXCLUDE_MODES,
  NUMBER_OPERATORS,
  describeCondition,
  emptyValue,
  fieldFor,
} from "../hooks/workingSetBuilder";

// Option lists longer than this get search + a checklist instead of a wall of pills.
const PILL_LIMIT = 12;

const INPUT =
  "h-9 rounded-lg border border-zinc-200 bg-white px-3 text-sm text-zinc-900 shadow-sm outline-none transition-colors placeholder:text-zinc-400 focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/5";

const matches = (text, query) => text.toLowerCase().includes(query.trim().toLowerCase());

const capitalise = (text) => text.charAt(0).toUpperCase() + text.slice(1);

const Segmented = ({ label, options, value, onChange }) => (
  <div className="inline-flex rounded-lg bg-zinc-100 p-0.5" role="radiogroup" aria-label={label}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        role="radio"
        aria-checked={value === o.value}
        onClick={() => onChange(o.value)}
        className={cn(
          "rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors",
          value === o.value ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-800",
        )}
      >
        {capitalise(o.label)}
      </button>
    ))}
  </div>
);

const NumberInput = ({ field, value, onChange, label, autoFocus }) => (
  <div className="relative flex items-center">
    {field.prefix && <span className="pointer-events-none absolute left-3 text-sm text-zinc-400">{field.prefix}</span>}
    <input
      type="number"
      inputMode="decimal"
      min="0"
      placeholder="0"
      aria-label={label}
      autoFocus={autoFocus}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(INPUT, "w-32", field.prefix && "pl-7", field.suffix && "pr-12")}
    />
    {field.suffix && <span className="pointer-events-none absolute right-3 text-sm text-zinc-400">{field.suffix}</span>}
  </div>
);

const NumberEditor = ({ field, value, onChange, autoFocus }) => (
  <div className="flex flex-wrap items-center gap-3">
    <Segmented
      label={`${field.title} comparison`}
      options={NUMBER_OPERATORS}
      value={value.operator}
      onChange={(operator) => onChange({ ...value, operator })}
    />
    <div className="flex items-center gap-2">
      {value.operator !== "lessThan" && (
        <NumberInput
          field={field}
          label={`${field.title} from`}
          value={value.from}
          autoFocus={autoFocus}
          onChange={(from) => onChange({ ...value, from })}
        />
      )}
      {value.operator === "between" && <span className="text-sm text-zinc-400">and</span>}
      {value.operator !== "greaterThan" && (
        <NumberInput
          field={field}
          label={`${field.title} to`}
          value={value.to}
          autoFocus={autoFocus && value.operator === "lessThan"}
          onChange={(to) => onChange({ ...value, to })}
        />
      )}
    </div>
  </div>
);

const PillSelect = ({ field, selected, onToggle }) => (
  <div className="flex flex-wrap gap-2" role="group" aria-label={field.title}>
    {field.options.map((o) => {
      const on = selected.includes(o.value);
      const Icon = on ? CheckIcon : PlusIcon;
      return (
        <button
          key={o.value}
          type="button"
          aria-pressed={on}
          onClick={() => onToggle(o.value)}
          className={cn(
            "inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors",
            on
              ? "border-zinc-900 bg-zinc-900 text-white"
              : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 hover:text-zinc-900",
          )}
        >
          {o.label}
          <Icon aria-hidden="true" className={cn("size-3.5", on ? "text-white" : "text-zinc-400")} />
        </button>
      );
    })}
  </div>
);

const SearchSelect = ({ field, selected, onToggle, autoFocus }) => {
  const [query, setQuery] = useState("");
  const shown = field.options.filter((o) => matches(o.label, query));
  const labelFor = (v) => field.options.find((o) => o.value === v)?.label ?? String(v);

  return (
    <div className="flex flex-col gap-2">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded-lg bg-zinc-900 py-1 pl-3 pr-1.5 text-[13px] font-medium text-white"
            >
              {labelFor(v)}
              <button
                type="button"
                aria-label={`Remove ${labelFor(v)}`}
                onClick={() => onToggle(v)}
                className="rounded-md p-0.5 text-zinc-300 hover:bg-zinc-700 hover:text-white"
              >
                <XMarkIcon className="size-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="overflow-hidden rounded-lg border border-zinc-200">
        <div className="relative border-b border-zinc-100">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-zinc-400" />
          <input
            type="search"
            aria-label={`Search ${field.title}`}
            placeholder={`Search ${field.options.length} ${field.title.toLowerCase()}…`}
            autoFocus={autoFocus}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-9 w-full bg-white pl-9 pr-3 text-sm outline-none placeholder:text-zinc-400"
          />
        </div>
        <ul className="max-h-48 overflow-y-auto py-1" role="listbox" aria-multiselectable="true" aria-label={field.title}>
          {shown.length === 0 ? (
            <li className="px-3 py-2 text-sm text-zinc-400">No matches</li>
          ) : (
            shown.map((o) => {
              const on = selected.includes(o.value);
              return (
                <li key={o.value} role="option" aria-selected={on}>
                  <button
                    type="button"
                    onClick={() => onToggle(o.value)}
                    className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm text-zinc-700 hover:bg-zinc-50"
                  >
                    <span className="truncate">{o.label}</span>
                    {on && <CheckIcon className="size-4 shrink-0 text-zinc-900" />}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>
  );
};

const OptionSelect = ({ field, selected, onChange, autoFocus }) => {
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return field.options.length > PILL_LIMIT ? (
    <SearchSelect field={field} selected={selected} onToggle={toggle} autoFocus={autoFocus} />
  ) : (
    <PillSelect field={field} selected={selected} onToggle={toggle} />
  );
};

// Groups: one include or exclude chain, matching what the Trade Accounts group filter sends.
const IncludeExcludeEditor = ({ field, value, onChange, autoFocus }) => {
  const chain = value[0] ?? { mode: "include", selected: [] };
  const set = (next) => onChange([{ ...chain, ...next }]);
  return (
    <div className="flex flex-col gap-3">
      <Segmented
        label={`${field.title} match`}
        options={INCLUDE_EXCLUDE_MODES}
        value={chain.mode}
        onChange={(mode) => set({ mode })}
      />
      <OptionSelect
        field={field}
        selected={chain.selected ?? []}
        autoFocus={autoFocus}
        onChange={(selected) => set({ selected })}
      />
    </div>
  );
};

const ConditionCard = ({ field, condition, onChange, onRemove, autoFocus }) => (
  <div className="flex flex-col gap-4 rounded-xl border border-zinc-200 bg-white p-5">
    <div className="flex items-center justify-between gap-2">
      <span className="flex items-baseline gap-2">
        <span className="text-sm font-semibold text-zinc-900">{field.title}</span>
        {field.type !== "number" && <span className="text-xs text-zinc-400">any of</span>}
      </span>
      <button
        type="button"
        aria-label={`Remove ${field.title}`}
        onClick={onRemove}
        className="rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
      >
        <XMarkIcon className="size-4" />
      </button>
    </div>
    {field.type === "enum" ? (
      <OptionSelect
        field={field}
        selected={condition.value}
        autoFocus={autoFocus}
        onChange={(value) => onChange({ ...condition, value })}
      />
    ) : field.type === "includeExcludeEnum" ? (
      <IncludeExcludeEditor
        field={field}
        value={condition.value}
        autoFocus={autoFocus}
        onChange={(value) => onChange({ ...condition, value })}
      />
    ) : (
      <NumberEditor
        field={field}
        value={condition.value ?? emptyValue(field)}
        autoFocus={autoFocus}
        onChange={(value) => onChange({ ...condition, value })}
      />
    )}
  </div>
);

// A filter brought in from a saved filter that the builder can't edit (e.g. last message sent).
// It's kept as-is and shown as a chip, so the set still matches the saved filter it started from.
const CarriedChip = ({ condition, onRemove }) => (
  <span className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white py-1 pl-3 pr-1.5 text-[13px] text-zinc-700">
    <span className="font-medium">{describeCondition(condition)}</span>
    <button
      type="button"
      aria-label={`Remove ${condition.title ?? condition.key}`}
      onClick={onRemove}
      className="rounded-md p-0.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
    >
      <XMarkIcon className="size-3.5" />
    </button>
  </span>
);

const AndDivider = () => (
  <div className="flex items-center gap-3 px-1" aria-hidden="true">
    <span className="h-px flex-1 bg-zinc-200" />
    <span className="text-[11px] font-medium uppercase tracking-wide text-zinc-400">and</span>
    <span className="h-px flex-1 bg-zinc-200" />
  </div>
);

// Every field the builder offers, grouped. A field already in the set shows as picked; picking it
// again takes its card away.
export const FieldRail = ({ fields, conditions, onToggle }) => {
  const [query, setQuery] = useState("");
  const usedKeys = new Set(conditions.map((c) => c.key));
  const grouped = useMemo(
    () =>
      FIELD_GROUPS.map((g) => ({
        ...g,
        fields: fields.filter((f) => f.group === g.key && matches(f.title, query)),
      })).filter((g) => g.fields.length > 0),
    [fields, query],
  );

  return (
    <nav className="flex flex-col gap-5 p-5" aria-label="Fields">
      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-zinc-400" />
        <input
          type="search"
          aria-label="Search fields"
          placeholder="Search fields"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className={cn(INPUT, "w-full pl-9")}
        />
      </div>
      {grouped.length === 0 ? (
        <p className="text-sm text-zinc-400">No fields match “{query}”.</p>
      ) : (
        grouped.map((g) => (
          <div key={g.key} className="flex flex-col gap-2">
            <span className="text-[11px] font-medium uppercase tracking-wide text-zinc-500">{g.label}</span>
            {g.fields.map((f) => {
              const on = usedKeys.has(f.key);
              const Icon = on ? CheckIcon : PlusIcon;
              return (
                <button
                  key={f.key}
                  type="button"
                  aria-pressed={on}
                  onClick={() => onToggle(f)}
                  className={cn(
                    "flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm font-medium transition-colors",
                    on
                      ? "border-zinc-900 bg-zinc-900 text-white"
                      : "border-zinc-200 bg-white text-zinc-800 hover:border-zinc-300",
                  )}
                >
                  <span className="truncate">{f.title}</span>
                  <Icon aria-hidden="true" className={cn("size-4 shrink-0", on ? "text-white" : "text-zinc-400")} />
                </button>
              );
            })}
          </div>
        ))
      )}
    </nav>
  );
};

const WorkingSetConditions = ({ fields, conditions, onChange, autoFocusKey }) => {
  const editable = conditions.filter((c) => fieldFor(fields, c.key));
  const carried = conditions.filter((c) => !fieldFor(fields, c.key));

  const replace = (key, next) => onChange(conditions.map((c) => (c.key === key ? next : c)));
  const remove = (key) => onChange(conditions.filter((c) => c.key !== key));

  if (conditions.length === 0) {
    return (
      <div className="flex flex-col items-center gap-1 rounded-xl border border-dashed border-zinc-300 px-6 py-10 text-center text-sm text-zinc-500">
        <span>Pick a field on the left.</span>
        <span>Each one becomes a card here.</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {editable.map((c, i) => (
        <div key={c.key} className="flex flex-col gap-3">
          {i > 0 && <AndDivider />}
          <ConditionCard
            field={fieldFor(fields, c.key)}
            condition={c}
            autoFocus={c.key === autoFocusKey}
            onChange={(next) => replace(c.key, next)}
            onRemove={() => remove(c.key)}
          />
        </div>
      ))}

      {carried.length > 0 && (
        <div className="flex flex-col gap-2">
          {editable.length > 0 && <AndDivider />}
          <div className="flex flex-wrap gap-2">
            {carried.map((c) => (
              <CarriedChip key={c.key} condition={c} onRemove={() => remove(c.key)} />
            ))}
          </div>
          <p className="text-xs text-zinc-400">Kept from your saved filter. Edit these on Trade accounts.</p>
        </div>
      )}
    </div>
  );
};

export default WorkingSetConditions;
